
import { Attachment } from '@/types';
import { generateId } from '@/lib/utils';

// IMPORTANT: react-pdftotext (and its transitive pdfjs-dist) is NOT
// statically imported here. pdfjs's compatibility shim runs an IIFE
// at module-load time that tries to require('canvas') for a DOMMatrix
// polyfill, which fails under Node SSR with "DOMMatrix is not defined"
// and kills entry-server.tsx's render() — forcing server.js into its
// empty-body SPA fallback that ships <div id="root"></div> with no
// SSR content (terrible for SEO). The dynamic `await import('react-pdftotext')`
// inside the PDF branch below means pdfjs only loads in the browser
// when a user actually attaches a PDF, never at server-render time.

export const readFileContent = async (file: File): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      if (event.target?.result) {
        if (file.type.includes('pdf') || file.name.endsWith('.pdf')) {
          try {
            console.log("PDF detected, attempting to extract text...");
            const { default: extractText } = await import('react-pdftotext');
            // Create a blob from the ArrayBuffer to use with extractText
            const arrayBuffer = event.target.result as ArrayBuffer;
            const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
            const text = await extractText(blob);
            
            if (text && text.trim().length > 0) {
              console.log(`Successfully extracted ${text.length} characters of text from PDF`);
              resolve(text);
            } else {
              console.warn("PDF text extraction returned empty text, using placeholder");
              const fileType = file.type || file.name.split('.').pop() || 'unknown';
              resolve(`[This is a PDF file: ${file.name}]\n\nFile size: ${(file.size / 1024).toFixed(2)} KB\nFile type: ${fileType}\n\nUnable to extract text content from this PDF.`);
            }
          } catch (error) {
            console.error("Error extracting PDF text:", error);
            const fileType = file.type || file.name.split('.').pop() || 'unknown';
            resolve(`[This is a PDF file: ${file.name}]\n\nFile size: ${(file.size / 1024).toFixed(2)} KB\nFile type: ${fileType}\n\nFailed to extract text content from this PDF.`);
          }
        } else if (file.type.startsWith('image/') || 
            file.type.includes('msword') || 
            file.type.includes('officedocument') || 
            file.type.includes('excel') || 
            file.type.includes('powerpoint') || 
            file.name.endsWith('.doc') || 
            file.name.endsWith('.docx') || 
            file.name.endsWith('.xls') || 
            file.name.endsWith('.xlsx') || 
            file.name.endsWith('.ppt') || 
            file.name.endsWith('.pptx')) {
          // For other binary files, provide a placeholder text
          const fileType = file.type || file.name.split('.').pop() || 'unknown';
          resolve(`[This is a binary ${fileType} file: ${file.name}]\n\nFile size: ${(file.size / 1024).toFixed(2)} KB\nFile type: ${fileType}\n\nThis content is a placeholder for the binary file. The actual file content cannot be displayed as text.`);
        } else {
          resolve(event.target.result as string);
        }
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    
    reader.onerror = (error) => {
      reject(error);
    };
    
    // For PDF files, read as ArrayBuffer for text extraction
    if (file.type.includes('pdf') || file.name.endsWith('.pdf')) {
      reader.readAsArrayBuffer(file);
    } else if (file.type.startsWith('image/') || 
        file.type.includes('msword') || 
        file.type.includes('officedocument') || 
        file.type.includes('excel') || 
        file.type.includes('powerpoint') || 
        file.name.endsWith('.doc') || 
        file.name.endsWith('.docx') || 
        file.name.endsWith('.xls') || 
        file.name.endsWith('.xlsx') || 
        file.name.endsWith('.ppt') || 
        file.name.endsWith('.pptx')) {
      reader.readAsArrayBuffer(file); // Just to trigger the onload event
    } else {
      reader.readAsText(file);
    }
  });
};

export const getFileType = (file: File): Attachment['type'] => {
  if (file.type.startsWith('image/')) {
    return 'image';
  } else if (
    file.type === 'text/plain' || 
    file.type === 'text/markdown' || 
    file.type === 'application/json' ||
    file.name.endsWith('.txt') ||
    file.name.endsWith('.md') ||
    file.name.endsWith('.json')
  ) {
    return 'code';
  } else if (file.type === 'application/pdf') {
    return 'file';
  }
  
  return 'file';
};

export const extractCodeSnippets = (messages: any[]) => {
  const snippets: Array<{id: string, code: string, language: string, name: string}> = [];
  
  messages.forEach(message => {
    if (message.attachments && message.attachments.length > 0) {
      message.attachments.forEach((attachment: any) => {
        if (attachment.type === 'code') {
          const language = attachment.name ? attachment.name.split('.').pop() || 'text' : 'text';
          const name = message.content || `Code snippet ${snippets.length + 1}`;
          
          snippets.push({
            id: attachment.id,
            code: attachment.content,
            language,
            name: name.replace("I've shared a code snippet in ", "").replace(":", "")
          });
        }
      });
    }
  });
  
  return snippets;
};

export const createAttachment = (
  type: Attachment['type'], 
  content: string, 
  name?: string
): Attachment => {
  return {
    id: generateId(),
    type,
    content,
    name,
  };
};

export const getDocumentFileSize = (content: string): number => {
  // Calculate file size in bytes (approximate)
  return new Blob([content]).size;
};

export const detectFileType = (filename: string): string => {
  const extension = filename.split('.').pop()?.toLowerCase();
  
  if (!extension) return 'markdown';
  
  switch (extension) {
    case 'txt':
      return 'text';
    case 'md':
      return 'markdown';
    case 'html':
      return 'html';
    case 'json':
      return 'json';
    case 'csv':
      return 'csv';
    default:
      return 'markdown';
  }
};
